import 'styles/earlybird/mobilepage.scss';
import { ReactComponent as GalleriIcon } from 'assets/images/iconsv2/galleri.svg';

import cls from 'classnames';
import { selectActiveProductInfo } from 'core/redux/features/products/reducers/productInfos';
import { selectActiveProduct } from 'core/redux/features/products/reducers/products';
import {
  NavTypes,
  actions as navActions,
} from 'core/redux/features/shared/nav';
import { AppState } from 'core/redux/reducers';
import { LocationUtil } from 'core/utils/location';
import { closest, height, listen, offset, scrollTop } from 'dom-helpers';
import React, {
  createRef,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { NavBurger } from '../../../Header/Nav';
import ProductImage from '../../Components/ProductImage';
import { OverlayTextDecider } from '../../OverlayText';
import { useHandleBackButton, useSelectViewModes } from '../../hooks';

function handleScrollEvent(scrollableContainerEl, contentRef) {
  const offsetTop = scrollTop(scrollableContainerEl);
  const offsetHeight = height(scrollableContainerEl);

  let contentOffset = null;
  if (contentRef.current) {
    contentOffset = offset(contentRef.current);
  }

  let pin = false;
  if (offsetTop >= 65) {
    pin = true;
  }

  let opacity;
  let colored = false;
  if (contentOffset && contentOffset.top <= 90) {
    colored = true;
  }

  if (pin) {
    opacity = offsetTop / (offsetHeight - height(contentRef.current));
    if (opacity > 0) {
      opacity += 0.5;
    }
  } else {
    opacity = 0.5;
  }

  const calcTop = Math.round((contentOffset?.top ?? 0) - 60);
  return {
    offset: offsetTop,
    pin,
    opacity,
    colored,
    contentOffsetTop: calcTop < 40 ? 0 : calcTop,
  };
}

type TProps = {
  activeViewModeId: string;
  mainRef: RefObject<HTMLDivElement>;
};

MobileHeader.defaultProps = {
  mainRef: createRef(),
};
export default function MobileHeader({ activeViewModeId, mainRef }: TProps) {
  //
  // html refs
  //
  // const mainRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null);

  //
  // 3rd party hooks
  //
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  //
  // selectors
  //
  const { viewMode: activeViewMode } = useSelectViewModes();
  const product = useSelector(selectActiveProduct);
  const productInfo = useSelector(selectActiveProductInfo);
  // const productTags = useSelector(selectProductTags)
  // const trans = useTrans()
  const { menuActive, deviceType } = useSelector((state: AppState) => ({
    menuActive: state.nav.active,
    deviceType: state.deviceType,
  }));

  //
  // event handlers
  //

  // handle open gallery on click
  const handleShowGallery = e => {
    e.preventDefault();
    const loc = new LocationUtil(location);
    loc.setHash('galleri');
    history.push(loc.toString());
  };

  // handle back button click
  const viewModeId = activeViewMode ? activeViewMode.id : null;
  const handleBackButton = useHandleBackButton(viewModeId);

  // handle click view modes nav menus
  // const handleViewModeChange = (e: React.SyntheticEvent, viewId: string) => {
  //   if (viewId === viewModeId) {
  //     e.preventDefault()
  //   }
  // }

  //
  // effects
  //

  const [{ pin, colored }, setCalculatedStyles] = useState({
    offset: 0,
    opacity: 0.5,
    pin: false,
    colored: false,
    contentOffsetTop: -200,
  });

  // Auto show product name in header when scroll down
  // it listen to scroll to calculate if the name show show it the header
  // the calculation includes:
  // opacity - for overlay color effect
  // contentOffsetTop - position of product name
  // colored - the header should show blue color
  // pin - the header should position to fix top
  const scrollableContainerEl = useRef(null);
  const removeScrollEvent = useRef(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (deviceType !== 'mobile') {
      return;
    }

    if (mainRef.current && !scrollableContainerEl.current) {
      scrollableContainerEl.current = closest(
        mainRef.current,
        '.pop-page-outer',
      ) as HTMLElement;
    }

    if (scrollableContainerEl.current && !removeScrollEvent.current) {
      removeScrollEvent.current = listen(
        scrollableContainerEl.current,
        'scroll',
        () => {
          setCalculatedStyles(
            handleScrollEvent(scrollableContainerEl.current, contentRef),
          );
        },
      );
    }
    return () => !removeScrollEvent ?? removeScrollEvent.current();
  }, [mainRef, scrollableContainerEl, removeScrollEvent, deviceType]);

  // const categories = useSelector(selectAllCategories)

  return (
    <>
      <div data-testid="productPage.mobileTop" className="m-head" ref={mainRef}>
        <div
          style={{ opacity: 0 }}
          className={cls('m-head__overlay', pin && 'head__overlay--blue')}
          onClick={handleShowGallery}
        />
        <div className="is-relative" onClick={handleShowGallery}>
          <ProductImage
            isLazy={false}
            // getParallaxEl={this.getParallaxEl}
            productId={product?.id}
          />
          {product ? <OverlayTextDecider productId={product.id} /> : null}
        </div>
      </div>

      <div
        className={cls(
          'm-nav_contain',
          activeViewModeId === 'gallery' && 'hide',
          pin && 'm-nav_contain--pin hide',
          colored && 'm-nav_contain--colored',
        )}
      >
        <div className="m-head__nav m-head__nav--left hide">
          <div onClick={handleBackButton}>
            <div className="eb-circle">
              <span className="eb eb-pl" />
            </div>
          </div>
        </div>
        <div className="m-head__nav m-head__nav--right">
          <div className="galleri-block" onClick={handleShowGallery}>
            <GalleriIcon />
            <span>{productInfo?.images?.length}</span>
          </div>
          <NavBurger
            isActive={menuActive}
            onClick={_ => {
              dispatch(navActions.toggle(NavTypes.Menu));
            }}
          />
        </div>
      </div>
      <div ref={contentRef} className="m-content">
        {/*<ProductName product={product} />*/}
        {/*<ProductAddress className="subtitle" product={product} />*/}
        {/*<ProductShortDescription product={product} />*/}
        {/*<ProductTime product={product} trans={trans} />*/}
        {/*/!*<div className="spacer" />*!/*/}
        {/*{product ? (*/}
        {/*  <ProductTags categories={categories} tags={productTags as ITags} />*/}
        {/*) : null}*/}
        {/*<div className="spacer" />*/}
        {/*<div className="grid-list m-buttons">*/}
        {/*  <NavLink*/}
        {/*    data-testid="productPage.viewDesc"*/}
        {/*    className="_item _50"*/}
        {/*    activeClassName="is-active"*/}
        {/*    isActive={() => activeViewModeId === 'desc'}*/}
        {/*    onClick={e => handleViewModeChange(e, 'desc')}*/}
        {/*    to={(() => {*/}
        {/*      const loc = new LocationUtil(location)*/}
        {/*      loc.setHash('')*/}
        {/*      return loc.toString()*/}
        {/*    })()}>*/}
        {/*    {trans('product_page.the_restaurant')}*/}
        {/*  </NavLink>*/}
        {/*  <NavLink*/}
        {/*    data-testid="productPage.viewMenu"*/}
        {/*    className="_item _50"*/}
        {/*    onClick={e => handleViewModeChange(e, 'menu')}*/}
        {/*    activeClassName="is-active"*/}
        {/*    isActive={() => activeViewModeId === 'menu'}*/}
        {/*    to={(() => {*/}
        {/*      const loc = new LocationUtil(location)*/}
        {/*      loc.setHash('menu')*/}
        {/*      return loc.toString()*/}
        {/*    })()}>*/}
        {/*    {trans('product_page.menu')}*/}
        {/*  </NavLink>*/}
        {/*</div>*/}
      </div>
    </>
  );
}
